import type { BrainQuality } from '@/systems/BrainSystem';
import type { SentienceLevel } from '@/types';

/**
 * Typed pub/sub for cross-cutting concerns (UI, autosave, achievements,
 * telemetry, tutorial). Deep modules continue to expose their own
 * local emitters; GameState bridges them to this bus.
 *
 * Add new event keys here. All typed — no stringly-typed payloads.
 */
export interface BusEvents {
  'mass:tier-change': { tier: number; mass: number };
  'brain:level-change': { level: number; value: number };
  'sentience:change': { level: SentienceLevel };
  'ability:unlocked': { abilityId: string };
  'consume:start': { preyId: string; speciesId: string };
  'consume:complete': {
    preyId: string;
    speciesId: string;
    biomassGained: number;
    brainGained: { quality: BrainQuality; mass: number };
  };
  'consume:interrupted': { preyId: string; progress: number };
  'blob:death': { causeSpeciesId: string };
  'blob:respawn': Record<string, never>;
  'tentacles:change': { count: number };
  'scene:enter': { sceneKey: string };
  'arc:enter': { arcId: string };
  'arc:complete': { arcId: string };
}

export type BusEventKey = keyof BusEvents;
export type BusListener<K extends BusEventKey> = (data: BusEvents[K]) => void;

export class EventBus {
  private readonly listeners = new Map<BusEventKey, Set<BusListener<BusEventKey>>>();

  emit<K extends BusEventKey>(key: K, data: BusEvents[K]): void {
    const set = this.listeners.get(key);
    if (!set) return;
    for (const listener of set) {
      (listener as BusListener<K>)(data);
    }
  }

  on<K extends BusEventKey>(key: K, listener: BusListener<K>): () => void {
    let set = this.listeners.get(key);
    if (!set) {
      set = new Set();
      this.listeners.set(key, set);
    }
    set.add(listener as BusListener<BusEventKey>);
    return () => {
      set!.delete(listener as BusListener<BusEventKey>);
    };
  }

  off<K extends BusEventKey>(key: K, listener: BusListener<K>): void {
    this.listeners.get(key)?.delete(listener as BusListener<BusEventKey>);
  }

  clear(): void {
    this.listeners.clear();
  }
}
